import { Skeleton, useToast } from '@chakra-ui/react';
import _ from 'lodash';
import React from 'react';

import { utils as commonUtils } from '@finley-technologies/common';

import { BorrowingBaseSummaryAccordionSubRow } from './BorrowingBaseSummaryAccordionSubRow';
import {
  BorrowingBaseSummaryLoanProductCalculationsAccordionRowFields,
  BorrowingBaseSummaryValueType,
  LoanProductCalculationsBorrowingBaseSummaryInput,
} from './utils';
import { errorToastConfig } from 'packages/common-frontend/constants';
import { useGetCreditFacilityQuery } from 'packages/credit-facility/requests/requests.gql.generated';

type BorrowingBaseSummaryReceivablesLoanProductCalculationsProps = {
  loanProductCalculations: LoanProductCalculationsBorrowingBaseSummaryInput;
  creditFacilityId: string;
  borrowingBaseReportField: keyof BorrowingBaseSummaryLoanProductCalculationsAccordionRowFields;
  valueType: BorrowingBaseSummaryValueType;
  showComparisonAndDifferenceColumns: boolean;
};
export const BorrowingBaseSummaryReceivablesLoanProductCalculations = ({
  loanProductCalculations,
  creditFacilityId,
  borrowingBaseReportField,
  valueType,
  showComparisonAndDifferenceColumns,
}: BorrowingBaseSummaryReceivablesLoanProductCalculationsProps) => {
  const toast = useToast();
  const {
    data: creditFacilityData,
    loading: isCreditFacilityLoading,
  } = useGetCreditFacilityQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = commonUtils.EnvUtils.isProduction()
        ? 'An unexpected error occurred. Please contact Finley for support.'
        : err.message;
      toast(errorToastConfig);
    },
    variables: { creditFacilityId },
  });

  if (!creditFacilityData || isCreditFacilityLoading) {
    return (
      <Skeleton
        height="600px"
        data-testid="borrowing-base-summary-receivables-loading"
      />
    );
  }

  const { loanProducts } = creditFacilityData.creditFacility;
  const loanProductIdToNameMap: { [key: string]: string } = {};
  _.forEach(loanProducts, ({ id, name }) => {
    loanProductIdToNameMap[id] = name;
  });

  return (
    <>
      {_.map(
        loanProductCalculations.minuendBorrowingBaseReport,
        (loanProductCalc) => {
          const loanProductId = loanProductCalc.loanProductId;
          const loanProductName = loanProductIdToNameMap[loanProductId];

          const minuendValue = loanProductCalc[borrowingBaseReportField];

          const subtrahendValue = _.find(
            loanProductCalculations.subtrahendBorrowingBaseReport,
            (lpCalcForBorrowingBaseReport2) =>
              lpCalcForBorrowingBaseReport2.loanProductId === loanProductId,
          )?.[borrowingBaseReportField];

          const referenceValue = _.find(
            loanProductCalculations.referenceBorrowingBaseReport,
            (lpCalcForBorrowingBaseReport2) =>
              lpCalcForBorrowingBaseReport2.loanProductId === loanProductId,
          )?.[borrowingBaseReportField];

          return (
            <BorrowingBaseSummaryAccordionSubRow
              key={`total-receivable-exposure-lp-${loanProductCalc.loanProductId}`}
              label={`${commonUtils.toTitleCase(loanProductName)}`}
              columnValues={{
                minuendValue,
                subtrahendValue,
                referenceValue,
              }}
              valueType={valueType}
              showComparisonAndDifferenceColumns={
                showComparisonAndDifferenceColumns
              }
              fieldName={borrowingBaseReportField}
            />
          );
        },
      )}
    </>
  );
};
